import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Divider, Form, message, Modal, Radio, Table, Typography } from 'antd';
import FileDownload from 'js-file-download';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from 'components/Spinner';
import { Button, Search } from 'ui';
import useQueryApiClient from 'utils/useApiClient';
import { useCustomer } from 'hooks/Customers/useCustomer';
import { ResourcePlannerAssignment, ResourcePlannerRow } from '../types';

interface SubordinateRow {
    id: number;
    user_id: number;
    position: string | null;
    department: string | null;
    org_unit: string | null;
    fullName?: string;
    name?: string;
    surname?: string;
    email?: string;
}

interface SubordinateState {
    data: SubordinateRow[];
    pagination: { current: number; pageSize: number; total: number };
    loading: boolean;
    search: string;
}

const DEFAULT_PAGE_SIZE = 10;

const ResourcePlanners: React.FC = () => {
    const { getResourcePlanners, getResourcePlannerSubordinates, loading } = useCustomer();
    const [rows, setRows] = useState<ResourcePlannerRow[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [subordinates, setSubordinates] = useState<Record<number, SubordinateState>>({});
    const [exportModalOpen, setExportModalOpen] = useState<boolean>(false);
    const [plannerSearch, setPlannerSearch] = useState<string>('');
    const [exportForm] = Form.useForm();
    const params = useParams();
    const customerId = params.id;
    const intl = useIntl();

    useEffect(() => {
        if (customerId) {
            load(customerId);
        }
    }, [customerId]);

    const load = async (id: string) => {
        const result = await getResourcePlanners(id);
        setRows(result.data);
    };

    const loadSubordinates = useCallback(
        async (plannerUserId: number, page: number, pageSize: number, search: string) => {
            if (!customerId) return;

            setSubordinates((prev) => ({
                ...prev,
                [plannerUserId]: {
                    data: prev[plannerUserId]?.data ?? [],
                    pagination: prev[plannerUserId]?.pagination ?? {
                        current: page,
                        pageSize,
                        total: 0,
                    },
                    loading: true,
                    search,
                },
            }));

            const result = await getResourcePlannerSubordinates(
                customerId,
                plannerUserId,
                page,
                pageSize,
                search,
            );

            setSubordinates((prev) => ({
                ...prev,
                [plannerUserId]: {
                    data: result.data,
                    pagination: {
                        current: result.pagination.current,
                        pageSize: result.pagination.pageSize,
                        total: result.pagination.total,
                    },
                    loading: false,
                    search,
                },
            }));
        },
        [customerId, getResourcePlannerSubordinates],
    );

    const onExpand = (expanded: boolean, record: ResourcePlannerRow) => {
        if (expanded && !subordinates[record.user_id]) {
            loadSubordinates(record.user_id, 1, DEFAULT_PAGE_SIZE, '');
        }
    };

    const handleSearch = useCallback(
        (plannerUserId: number, encoded: string) => {
            setSubordinates((prev) => {
                const state = prev[plannerUserId];
                const pageSize = state?.pagination.pageSize ?? DEFAULT_PAGE_SIZE;
                const decoded = decodeURIComponent(encoded);
                loadSubordinates(plannerUserId, 1, pageSize, decoded);
                return prev;
            });
        },
        [loadSubordinates],
    );

    const { appendData: exportSubordinates, isLoading: exportLoading } = useQueryApiClient({
        request: {
            url: `/api/v1/customers/:customerId/resource-planners/export`,
            method: 'POST',
            mustRetry: true,
            multipart: true,
        },
        onSuccess: (response) => {
            if (response) {
                FileDownload(response, 'resource_planner_subordinates.xlsx');
                message.success(intl.formatMessage({ id: 'users.export.successful_export_in_message' }));
            }
        },
    });

    const handleExport = (values: { exportOption: 'all' | 'selected' }) => {
        if (!customerId) return;

        if (values.exportOption === 'selected' && selectedRowKeys.length === 0) {
            message.error(intl.formatMessage({ id: 'campaign.please_select_atleast_one_user' }));
            return;
        }

        const bodyFormData = new FormData();
        if (values.exportOption === 'selected') {
            selectedRowKeys.forEach((id) => bodyFormData.append('plannerUserIds[]', String(id)));
        }
        bodyFormData.append('locale', intl.locale);

        exportSubordinates(bodyFormData, { customerId });

        setExportModalOpen(false);
        exportForm.resetFields();
    };

    const filteredRows = React.useMemo(() => {
        const q = plannerSearch.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((r) => {
            const haystack = [
                r.fullName,
                r.name,
                r.surname,
                r.email,
                r.department,
                r.org_unit,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(q);
        });
    }, [rows, plannerSearch]);

    const columns = [
        {
            title: intl.formatMessage({ id: 'general.full_name' }),
            dataIndex: 'fullName',
            sorter: (a: ResourcePlannerRow, b: ResourcePlannerRow) =>
                (a.fullName || '').localeCompare(b.fullName || ''),
            render: (_text: string, record: ResourcePlannerRow) =>
                record.fullName || record.email || `ID: ${record.user_id}`,
        },
        {
            title: intl.formatMessage({ id: 'general.email' }),
            dataIndex: 'email',
        },
        {
            title: intl.formatMessage({ id: 'general.departments' }),
            dataIndex: 'department',
            render: (value: string | null) => value || '-',
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            dataIndex: 'org_unit',
            render: (value: string | null) => value || '-',
        },
        {
            title: intl.formatMessage({ id: 'general.users' }),
            dataIndex: 'user_count',
            sorter: (a: ResourcePlannerRow, b: ResourcePlannerRow) => a.user_count - b.user_count,
            defaultSortOrder: 'descend' as const,
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: React.Key[]) => {
            setSelectedRowKeys(keys);
        },
        preserveSelectedRowKeys: true,
    };

    const assignmentColumns = [
        {
            title: intl.formatMessage({ id: 'general.departments' }),
            dataIndex: 'department',
            render: (value: string | null, record: ResourcePlannerAssignment) =>
                record.is_direct ? intl.formatMessage({ id: 'general.direct' }) : (value || '-'),
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            dataIndex: 'org_unit',
            render: (value: string | null) => value || '-',
        },
        {
            title: intl.formatMessage({ id: 'general.users' }),
            dataIndex: 'user_count',
        },
    ];

    const subordinateColumns = [
        {
            title: intl.formatMessage({ id: 'general.full_name' }),
            dataIndex: 'fullName',
            render: (_text: string, record: SubordinateRow) =>
                record.fullName || record.email || `ID: ${record.user_id}`,
        },
        {
            title: intl.formatMessage({ id: 'general.departments' }),
            dataIndex: 'department',
            render: (value: string | null) => value || '-',
        },
        {
            title: intl.formatMessage({ id: 'general.organizational_unit' }),
            dataIndex: 'org_unit',
            render: (value: string | null) => value || '-',
        },
        {
            title: intl.formatMessage({ id: 'general.position' }),
            dataIndex: 'position',
            render: (value: string | null) => value || '-',
        },
    ];

    const renderExpanded = (record: ResourcePlannerRow) => {
        const sub = subordinates[record.user_id];

        return (
            <div
                style={{
                    padding: 16,
                    margin: '8px 0',
                    border: '1px solid #d9d9d9',
                    borderRadius: 6,
                    background: '#fafafa',
                    boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.02)',
                }}
            >
                <Typography.Title level={5} style={{ marginTop: 0, marginBottom: 8 }}>
                    {intl.formatMessage({ id: 'general.resource_planners' })}
                </Typography.Title>
                <Table
                    size="small"
                    columns={assignmentColumns}
                    rowKey={'structure_id'}
                    pagination={false}
                    dataSource={record.assignments || []}
                />

                <Divider style={{ margin: '24px 0' }} />

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 16 }}>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        {intl.formatMessage({ id: 'general.users' })}
                    </Typography.Title>
                    <div style={{ maxWidth: 320, width: '100%' }}>
                        <Search
                            setSearchString={(value: React.SetStateAction<string>) => {
                                const resolved = typeof value === 'function' ? value(sub?.search ?? '') : value;
                                handleSearch(record.user_id, resolved);
                            }}
                        />
                    </div>
                </div>
                <Table
                    columns={subordinateColumns}
                    rowKey={'id'}
                    loading={sub?.loading ?? false}
                    dataSource={sub?.data ?? []}
                    pagination={{
                        current: sub?.pagination.current ?? 1,
                        pageSize: sub?.pagination.pageSize ?? DEFAULT_PAGE_SIZE,
                        total: sub?.pagination.total ?? 0,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50'],
                    }}
                    onChange={(pagination) =>
                        loadSubordinates(
                            record.user_id,
                            pagination.current ?? 1,
                            pagination.pageSize ?? DEFAULT_PAGE_SIZE,
                            sub?.search ?? '',
                        )
                    }
                />
            </div>
        );
    };

    return (
        <Spinner spinning={loading}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 16 }}>
                <Button
                    type={'primary'}
                    onClick={() => setExportModalOpen(true)}
                    message='users.export.export'
                />
                <div style={{ maxWidth: 320, width: '100%' }}>
                    <Search setSearchString={setPlannerSearch} />
                </div>
            </div>
            <Table
                columns={columns}
                rowKey={'user_id'}
                pagination={false}
                dataSource={filteredRows}
                rowSelection={rowSelection}
                expandable={{
                    expandedRowRender: renderExpanded,
                    onExpand,
                }}
            />

            <Modal
                title={intl.formatMessage({ id: 'users.export.export' })}
                open={exportModalOpen}
                onOk={() => exportForm.submit()}
                confirmLoading={exportLoading}
                onCancel={() => {
                    setExportModalOpen(false);
                    exportForm.resetFields();
                }}
                okText={intl.formatMessage({ id: 'general.export.send' })}
                cancelText={intl.formatMessage({ id: 'general.cancel' })}
            >
                <Form
                    form={exportForm}
                    initialValues={{ exportOption: 'all' }}
                    onFinish={handleExport}
                >
                    <Form.Item
                        name="exportOption"
                        label={intl.formatMessage({ id: 'users.export.export_option' })}
                    >
                        <Radio.Group>
                            <Radio value="all">
                                <FormattedMessage id='users.export.export_all_users' />
                            </Radio>
                            <Radio value="selected">
                                <FormattedMessage
                                    id='users.export.export_selected_users'
                                    values={{ amount: selectedRowKeys.length }}
                                />
                            </Radio>
                        </Radio.Group>
                    </Form.Item>
                </Form>
            </Modal>
        </Spinner>
    );
};

export default ResourcePlanners;
